import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const BASE_URL = 'https://opusmagnumghost.com'

const routeMeta = {
  '/': {
    title: 'Opus Magnum Ghost — O.M.G.',
    description: 'Extrait strength. Whisper distance. Six variants built on one molecular foundation — from skin-close oil to room-filling spray. Not a fragrance you wear. The version of you that people remember.',
  },
  '/privacy': {
    title: 'Privacy Policy — Opus Magnum Ghost',
    description: 'How Opus Magnum Ghost collects, uses, and protects your personal data under GDPR and EU regulations.',
  },
  '/terms': {
    title: 'Terms & Conditions — Opus Magnum Ghost',
    description: 'Terms and conditions for purchasing from Opus Magnum Ghost. EU consumer rights, returns, and legal obligations.',
  },
  '/returns': {
    title: 'Returns Policy — Opus Magnum Ghost',
    description: 'Our returns and refund policy. 14-day EU withdrawal right and satisfaction guarantee.',
  },
  '/shipping': {
    title: 'Shipping Information — Opus Magnum Ghost',
    description: 'Shipping rates, delivery times, and international availability. Free shipping on Sovereign and Apex tiers.',
  },
  '/aviso-legal': {
    title: 'Aviso Legal — Opus Magnum Ghost',
    description: 'Aviso legal y condiciones de uso del sitio web opusmagnumghost.com.',
  },
  '/withdrawal': {
    title: 'Right of Withdrawal — Opus Magnum Ghost',
    description: 'Your 14-day right of withdrawal under EU consumer protection law. How to cancel and return your order.',
  },
  '/layering': {
    title: 'Fragrance Layering Guide — Opus Magnum Ghost',
    description: 'How to layer Ghost fragrances for maximum impact. Seven proven stacks from daily wear to full intimate layering. Spray to arrive, oil to remain.',
  },
  '/ingredients': {
    title: 'Ingredients — The Science Behind Opus Magnum Ghost',
    description: 'Every molecule explained. Iso E Super, Ambroxan, Hedione, Muscone, Cashmeran — the science behind why Ghost smells like you, not perfume.',
  },
  '/about': {
    title: 'About Opus Magnum Ghost — The Fragrance System',
    description: 'One molecular foundation, six variants, three distances. Extrait-strength fragrance engineered to be attributed to the wearer, never the bottle.',
  },
  '/ecosystem': {
    title: 'The Ecosystem — Opus Magnum Ghost Body Care & Fabric Care',
    description: 'Skin primer, deodorant, fabric neutralizer, body wash, shampoo, dryer balls — every product in the Ghost ecosystem. Eliminate competing scent.',
  },
  '/blog': {
    title: 'Blog — Fragrance Science & Guides — Opus Magnum Ghost',
    description: 'Ingredient deep-dives, fragrance layering guides, and the molecular science behind modern perfumery. Every molecule explained.',
  },
}

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Opus Magnum Ghost',
  alternateName: 'O.M.G.',
  url: BASE_URL,
  logo: `${BASE_URL}/monolith-2.jpg`,
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'ghost@opusmagnumghost.com',
    contactType: 'customer service',
  },
}

const productSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'The Diagnostic Set',
  description: 'Ghost 10ml plus 2ml of each main variant. The system in miniature. Extrait de Parfum.',
  brand: { '@type': 'Brand', name: 'Opus Magnum Ghost' },
  offers: {
    '@type': 'Offer',
    price: '65.00',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/PreOrder',
    url: `${BASE_URL}/login`,
  },
  image: `${BASE_URL}/monolith-2.jpg`,
  category: 'Fragrances',
}

export default function SEO() {
  const { pathname } = useLocation()
  const { i18n } = useTranslation()
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en'
  const meta = routeMeta[pathname] || routeMeta['/']
  const canonical = `${BASE_URL}${pathname === '/' ? '' : pathname}`
  const isHome = pathname === '/'

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${BASE_URL}/monolith-2.jpg`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={`${BASE_URL}/monolith-2.jpg`} />
      {isHome && (
        <script type="application/ld+json">
          {JSON.stringify([organizationSchema, productSchema])}
        </script>
      )}
    </Helmet>
  )
}
